<template>
	<div class="content re" v-if="loaded">
		<!-- ── 리포트 설정 ──────────────────────────────────────────────────────
		     콘솔의 입력 표(라벨칸 + 값칸)다. 제목·상태·브리핑·지면이 한 표에 서야
		     "이 리포트의 설정" 으로 읽힌다(고객 문서 위의 옵션이 아니다). -->
		<div class="section">
			<div class="section-tit">
				Weekly 편집
				<span class="sub">{{ report.period_key }} · {{ ymd(report.sdate) }} ~ {{ ymd(report.edate) }}</span>
				<div class="right">
					<button type="button" class="btn btn-sm btn-light" @click="preview">고객 화면 미리보기</button>
					<button type="button" class="btn btn-sm btn-light" @click="refreshItems" :disabled="busy" v-if="canEdit">매물정보 새로고침</button>
					<button type="button" class="btn btn-sm btn-primary" @click="save" :disabled="busy || !canEdit">저장</button>
				</div>
			</div>

			<!-- 리포트 내용은 작성자 것이지만 고객 링크는 담당자별이다(CR). 공용 Weekly(DE)는 관리자 자료라 내용은
			     관리자만 고치고, 직원은 각자 내 고객 링크를 발급한다. 그 사실을 여기서 알려야 같은 리포트를 또 만들지 않는다. -->
			<p class="re-readonly" v-if="!canEdit && shared">
				관리자가 공용으로 지정한 <b>공용 Weekly</b> 입니다. 내용은 관리자만 고칠 수 있고,
				아래 <b>내 고객 링크</b>에서 내 고객에게 보낼 링크는 바로 발급할 수 있습니다.
			</p>
			<p class="re-readonly" v-else-if="!canEdit && !canIssue">
				공용 지정이 풀린 Weekly 입니다. 새 링크는 발급할 수 없고, 이미 보낸 내 고객 링크의 열람과 회수만 할 수 있습니다.
			</p>
			<p class="re-readonly" v-else-if="!canEdit">
				이 리포트는 <b>{{ report.created_name || '다른 직원' }}</b>님이 작성해 내용은 작성자만 고칠 수 있습니다.
				아래 <b>내 고객 링크</b>에서 내 고객에게 보낼 링크는 바로 발급할 수 있습니다.
			</p>

			<table class="table type-input re-form">
				<colgroup><col class="re-col-label" /><col /></colgroup>
				<tr>
					<th>제목</th>
					<td>
						<div class="re-row">
							<input type="text" v-model="report.title" class="form-control re-title" maxlength="120" :disabled="!canEdit || busy" />
							<select v-model="report.status" class="form-select form-select-sm re-status" :disabled="!canEdit || busy">
								<option value="draft">작성중</option>
								<option value="published">발행</option>
							</select>
							<span class="re-hint" v-if="report.status !== 'published'">발행으로 바꿔 저장해야 고객 링크가 열립니다.</span>
						</div>
					</td>
				</tr>
				<!-- 공용 Weekly(DE). 지정·해제는 관리자만 하고 바로 반영된다(저장 버튼과 따로다. 내용이 아니라 공유 범위다). -->
				<tr v-if="canShare || shared">
					<th>공용 Weekly</th>
					<td>
						<div class="re-row">
							<template v-if="canShare">
								<label class="re-share">
									<input type="checkbox" :checked="shared" :disabled="busy || sharing" @change="toggleShare($event)" />
									공용으로 공유
								</label>
								<span class="re-hint">켜면 Weekly 권한이 있는 모든 직원의 목록에 보이고, 각자 자기 고객 링크를 발급합니다. 공용인 동안 내용은 관리자만 고칩니다.</span>
							</template>
							<span v-else class="re-hint">관리자가 공유한 Weekly 입니다. 내 고객 링크만 보입니다.</span>
						</div>
					</td>
				</tr>
				<tr>
					<th>Weekly Brief</th>
					<td>
						<textarea v-model="report.brief_final" class="form-control textarea" rows="3" maxlength="2000"
							:disabled="!canEdit || busy" placeholder="이번 주 매물 묶음을 소개하는 2~3문장"></textarea>
						<div class="re-draft" v-if="report.brief_ai && report.brief_ai !== report.brief_final">
							<span class="re-draft__label">AI 초안</span>{{ report.brief_ai }}
							<button type="button" class="re-link" @click="report.brief_final = report.brief_ai" v-if="canEdit" :disabled="busy">되돌리기</button>
						</div>
						<div class="re-ai" v-if="canEdit">
							<button type="button" class="btn btn-sm btn-secondary" @click="genAi" :disabled="busy || !aiEnabled">
								{{ busy ? '생성 중…' : 'AI 문구 초안 생성' }}
							</button>
							<span class="re-hint" v-if="!aiEnabled">AI 문구 생성이 설정되어 있지 않습니다. 문구는 직접 입력하시면 됩니다.</span>
							<span class="re-hint" v-else>가격·면적·수익률은 데이터베이스 값이 그대로 표시되며, AI 는 브리핑과 매물 코멘트 문장만 작성합니다.</span>
						</div>
					</td>
				</tr>
				<!-- 지면 디자인. **고객 문서 위가 아니라 여기서 정한다**. 리포트의 설정이지 출력할 때
				     고르는 옵션이 아니다. 견본은 표지 골격을 작게 그린 것이라 이름보다 먼저 읽힌다. -->
				<tr>
					<th>지면 디자인</th>
					<td>
						<ul class="th-list">
							<li v-for="t in themes" :key="t.id">
								<button
									type="button"
									class="th-opt"
									:class="{ on: report.theme === t.id }"
									:disabled="!canEdit || busy"
									:aria-pressed="report.theme === t.id"
									@click="report.theme = t.id">
									<span class="th-cover" :class="'th-cover--' + t.id" aria-hidden="true">
										<i v-for="n in 9" :key="n"></i>
									</span>
									<span class="th-txt">
										<strong>{{ t.name }}</strong>
										<span>{{ t.note }}</span>
									</span>
								</button>
							</li>
						</ul>
						<div class="re-hint re-hint--row">
							고른 지면은 저장해야 고객 링크와 PDF 에 반영됩니다.
							<button type="button" class="re-link" @click="preview" v-if="links.length">이 지면으로 미리보기</button>
						</div>
						<!-- 항공사진 표지는 매물 좌표로 지도를 그린다. 좌표가 담기기 전(2026-09-15)에 만든
						     리포트는 자료에 좌표가 없어 지도 대신 대표 사진이 실린다. 담당자가 그 사실을
						     여기서 알아야 새로고침을 누른다. -->
						<div class="re-hint re-hint--warn" v-if="report.theme === 'aerial' && noSpotCnt">
							이 리포트 자료에는 매물 {{ noSpotCnt }}건의 좌표가 없어 항공사진 표지에 지도 대신 대표 사진이 실립니다.
							<template v-if="canEdit">위의 '매물정보 새로고침' 을 누르면 좌표를 다시 담습니다.</template>
						</div>
					</td>
				</tr>
			</table>
		</div>

		<!-- ── 수록 매물 ────────────────────────────────────────────────────────
		     대장의 행처럼 번호·사진·이름·제원이 한 줄에 서고, 그 아래에 이 리포트에서만
		     쓰는 것(갈래·코멘트·특징 노출)이 붙는다. 순서가 곧 리포트의 물건 번호다. -->
		<div class="section">
			<div class="section-tit">
				수록 매물
				<span class="sub">{{ items.length }}건 · 위에서부터 물건 01, 02… 순서로 실립니다</span>
				<span class="sub lot__order-note" v-if="orderChanged">순서를 바꿨습니다. 저장을 눌러야 고객 화면에 반영됩니다</span>
			</div>

			<div class="lot" v-for="(it, idx) in items" :key="it.item_uid">
				<div class="lot__no">{{ String(idx + 1).padStart(2, '0') }}</div>
				<div class="lot__thumb">
					<img v-if="thumb(it)" :src="thumb(it)" alt="" />
					<div v-else class="lot__noimg">사진 없음</div>
				</div>
				<div class="lot__main">
					<div class="lot__head">
						<span class="lot__name">{{ it.snapshot.building_name || '매물' }}</span>
						<span class="lot__addr">{{ it.snapshot.jibun_addr || '' }}</span>
						<!-- 순서는 화면에서 바꾸고 저장으로 반영한다. 빼기는 바로 반영된다(서버가 행을 지운다). -->
						<span class="lot__ops" v-if="canEdit">
							<!-- 저장·생성 중에는 막는다. 응답 전에 옮기면 옛 순서가 저장되고 안내만 지워진다. -->
							<button type="button" class="btn btn-xsm btn-outline-secondary" :disabled="busy || idx === 0" @click="moveItem(idx, -1)">위로</button>
							<button type="button" class="btn btn-xsm btn-outline-secondary" :disabled="busy || idx + 1 === items.length" @click="moveItem(idx, 1)">아래로</button>
							<button
								type="button"
								class="btn btn-xsm btn-outline-danger"
								:disabled="busy || items.length <= 1"
								:title="items.length <= 1 ? '리포트에 매물이 한 건은 있어야 합니다' : ''"
								@click="removeItem(it)"
							>빼기</button>
						</span>
					</div>
					<dl class="lot__spec">
						<div><dt>매매가</dt><dd>{{ won(it.snapshot.sell_price) }}</dd></div>
						<div><dt>대지</dt><dd>{{ it.snapshot.land_size_p ? it.snapshot.land_size_p + '평' : '-' }}</dd></div>
						<div><dt>연면적</dt><dd>{{ it.snapshot.total_size_p ? it.snapshot.total_size_p + '평' : '-' }}</dd></div>
						<div><dt>수익률</dt><dd>{{ it.snapshot.earning_rate != null ? it.snapshot.earning_rate + '%' : '-' }}</dd></div>
					</dl>

					<!-- 갈래(태그). 필터 칩과 같은 생김새(DESIGN.md filter-chip). 켠 것만 색을 입는다. -->
					<div class="lot__tags">
						<label v-for="t in tagCodes" :key="t" class="lot__chip" :class="{ on: it.tags.includes(t), off: !canEdit }">
							<input type="checkbox" :value="t" v-model="it.tags" :disabled="!canEdit || busy" />{{ t }}
						</label>
					</div>

					<div class="lot__field">
						<span class="lot__label">코멘트</span>
						<input type="text" v-model="it.comment_final" class="form-control" maxlength="300"
							:disabled="!canEdit || busy" placeholder="고객에게 보여줄 한 줄 코멘트" />
					</div>
					<div class="re-draft" v-if="it.comment_ai && it.comment_ai !== it.comment_final">
						<span class="re-draft__label">AI 초안</span>{{ it.comment_ai }}
						<button type="button" class="re-link" @click="it.comment_final = it.comment_ai" v-if="canEdit" :disabled="busy">되돌리기</button>
					</div>

					<!-- 매물 특징(사내 메모)은 기본적으로 고객에게 보내지 않는다. 담당자가 내용을
					     눈으로 확인하고 켠 것만 나가도록 미리보기를 함께 둔다. -->
					<div class="lot__feat" v-if="(it.snapshot.feature_lines || []).length" :class="{ on: it.show_features }">
						<label class="input-checkbox lot__feat-toggle">
							<input type="checkbox" v-model="it.show_features" :disabled="!canEdit || busy" />
							<span class="checkbox"></span>
							<span class="text">매물 특징을 고객에게 함께 보여주기</span>
							<span class="lot__feat-state">{{ it.show_features ? '고객에게 나갑니다' : '지금은 사내에서만 보입니다' }}</span>
						</label>
						<p class="lot__feat-warn" v-if="!it.show_features">
							아래 내용은 사내 메모라 매도인 사정·과거 매각가 같은 협상 정보가 섞여 있을 수 있습니다. 내용을 확인하신 뒤 켜 주세요.
						</p>
						<ul class="lot__feat-list">
							<li v-for="(line, i) in it.snapshot.feature_lines" :key="i">{{ line }}</li>
						</ul>
					</div>
				</div>
			</div>
		</div>

		<!-- ── 고객 링크 + 관심도 ──────────────────────────────────────────────
		     본문 표(링크)와 우측 보조 패널(관심도)의 콘솔 배치(DESIGN.md Layout).
		     고객은 담당자별이다(CR, 2026-09-23): 직원은 자기가 발급한 링크와 그 열람만 보고, 어드민(master)은
		     전체를 담당자 칩으로 걸러 본다. 범위는 서버가 정한다(routes/report.js 의 linkScope). -->
		<div class="section-wrap section-wrap--aside">
			<div class="section">
				<div class="section-tit">
					{{ allLinks ? '고객별 링크' : '내 고객 링크' }}
					<span class="sub" v-if="allLinks">전 담당자가 발급한 링크입니다. 담당자를 고르면 그 담당자의 고객만 봅니다</span>
					<span class="sub" v-else>내가 발급한 링크만 보입니다. 다른 담당자의 고객은 서로 보이지 않습니다</span>
				</div>

				<!-- 담당자별(어드민). 결과를 좁히는 2차 필터라 탭이 아니라 칩이다(DESIGN.md '탭과 2차 필터의 위계'). -->
				<div class="re-staff" v-if="allLinks && staff.length">
					<span class="re-staff__label">담당자별</span>
					<button type="button" class="btn re-staff__chip" :class="{ on: !memFilter }" @click="pickStaff(null)">
						전체<span class="re-staff__cnt">{{ staffTotal }}</span>
					</button>
					<button
						v-for="st in staff"
						:key="'st' + st.mem_uid"
						type="button"
						class="btn re-staff__chip"
						:class="{ on: Number(memFilter) === Number(st.mem_uid) }"
						:title="`${st.name} 링크 ${st.link_cnt}개 · 열어 본 고객 ${st.viewed_cnt}명`"
						@click="pickStaff(st.mem_uid)">
						{{ st.name }}<span class="re-staff__cnt">{{ st.link_cnt }}</span>
					</button>
				</div>

				<!-- 발급은 작성자가 아니어도 한다(공용 Weekly). 고객은 각자의 것이다. 공용이 풀린 남의 리포트에는 새로 내지 않는다. -->
				<div class="re-issue" v-if="canIssue">
					<label class="re-issue__label" for="re-link-label">고객명</label>
					<input id="re-link-label" type="text" v-model="linkLabel" class="form-control re-issue__name" maxlength="80"
						placeholder="예) 김○○ 대표님" @keyup.enter="addLink" />
					<label class="re-issue__label" for="re-link-days">유효기간</label>
					<select id="re-link-days" v-model.number="linkDays" class="form-select form-select-sm re-issue__days">
						<option :value="30">30일</option>
						<option :value="90">90일</option>
						<option :value="180">180일</option>
						<option :value="365">365일</option>
					</select>
					<button type="button" class="btn btn-sm btn-primary re-issue__btn" @click="addLink" :disabled="busy">링크 발급</button>
					<span class="re-hint">발급하면 링크가 바로 복사됩니다. 고객 화면에는 발급한 사람이 담당자로 적힙니다.</span>
				</div>

				<div class="table-wrap">
					<table class="table sm">
						<thead>
							<tr>
								<th>고객</th>
								<th v-if="allLinks">담당자</th>
								<th>링크</th>
								<th>최초 열람</th>
								<th>최근 열람</th>
								<th>열람</th>
								<th>총 체류</th>
								<th>PDF</th>
								<th>상태</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="l in links" :key="l.link_uid" :class="{ 'is-off': l.revoked_at || expired(l) }">
								<td class="left"><b>{{ l.client_label }}</b></td>
								<td v-if="allLinks">{{ l.created_name }}</td>
								<td>
									<button type="button" class="re-link re-link--cell" @click="copyLink(l)" v-if="l.token">링크 복사</button>
									<span v-else class="re-muted">-</span>
								</td>
								<td class="num">{{ dt(l.first_viewed_at) }}</td>
								<td class="num">{{ dt(l.last_viewed_at) }}</td>
								<td class="num" :class="{ zero: !Number(l.view_cnt) }">{{ l.view_cnt }}회</td>
								<td class="num">{{ dur(l.active_ms) }}</td>
								<td class="num">{{ Number(l.pdf_cnt) ? l.pdf_cnt + '회' : '-' }}</td>
								<td>
									<span v-if="l.revoked_at" class="re-state is-off">회수됨</span>
									<span v-else-if="expired(l)" class="re-state is-off">기간만료</span>
									<span v-else class="re-state is-on">유효</span>
								</td>
								<td>
									<button type="button" class="btn btn-xxsm btn-light" @click="toggleLink(l)" v-if="l.can_manage">
										{{ l.revoked_at ? '되살리기' : '회수' }}
									</button>
								</td>
							</tr>
							<tr v-if="!links.length">
								<td :colspan="allLinks ? 10 : 9" class="re-empty">
									<template v-if="allLinks && memFilter">{{ memName }}님이 이 리포트로 발급한 링크가 없습니다.</template>
									<template v-else>아직 발급한 링크가 없습니다. 위에서 고객명을 넣고 발급하세요.</template>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div class="section section--aside">
				<div class="section-tit">
					매물별 관심도
					<span class="sub">{{ interestScope }}</span>
				</div>
				<p class="re-hint re-hint--block">
					고객 화면에 그 매물이 실제로 보인 시간입니다. 스크롤로 지나간 시간과 자리를 비운 시간은 빠져 있어
					절대값보다 <b>같은 리포트 안에서의 비교</b>로 보세요.
					<span v-if="botExcluded">카톡 미리보기 등 자동 접속 {{ botExcluded }}건은 뺐습니다.</span>
				</p>
				<!-- 보조 패널은 열 셋 이하(DESIGN.md). 이름 아래에 체류·본 사람·사진 확대를 한 줄로 적고,
				     오른쪽은 같은 리포트 안의 상대 비중 막대 하나다. -->
				<table class="table sm re-interest">
					<colgroup><col /><col style="width: 96px" /></colgroup>
					<thead>
						<tr><th>매물</th><th>비중</th></tr>
					</thead>
					<tbody>
						<tr v-for="s in itemStats" :key="s.building_uid">
							<td class="left">
								<b class="re-interest__name" :title="nameOf(s.building_uid)">{{ nameOf(s.building_uid) }}</b>
								<span class="re-interest__sub">{{ interestSub(s) }}</span>
							</td>
							<td class="re-bar-cell">
								<span class="re-bar"><span class="re-bar__fill" :style="{ width: pct(s.visible_ms) + '%' }"></span></span>
								<span class="re-bar__num">{{ pct(s.visible_ms) }}%</span>
							</td>
						</tr>
						<tr v-if="!itemStats.length"><td colspan="2" class="re-empty">아직 열람 기록이 없습니다.</td></tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- 긴 화면이라 저장은 아래에도 있다. 삭제는 반대편 끝에 떨어뜨려 손이 가지 않게 한다. -->
		<div class="section re-foot" v-if="canEdit">
			<button type="button" class="btn btn-sm btn-danger" @click="removeReport">리포트 삭제</button>
			<div class="re-foot__main">
				<button type="button" class="btn btn-sm btn-light" @click="preview">고객 화면 미리보기</button>
				<button type="button" class="btn btn-sm btn-primary" @click="save" :disabled="busy">저장</button>
			</div>
		</div>
	</div>
</template>

<script>
import { formatMoneyMuch } from '@/utils/format';
import { REPORT_THEMES, DEFAULT_THEME, themeOf } from '@/utils/reportThemes';

export default {
	name: 'ReportEdit',
	data() {
		return {
			loaded: false,
			busy: false,
			// 수록 매물 순서를 화면에서 바꾸고 아직 저장하지 않았는가(저장해야 고객 화면 번호가 바뀐다).
			// 진행 중인 작업 번호. 작업마다 하나씩 올리고 리포트를 옮기면 또 올린다. 끝난 작업은 자기 번호가 지금 번호일
			// 때만 잠금을 풀고 화면을 건드린다(A 의 늦은 응답이 B 의 잠금을 풀거나, A→B→A 로 돌아온 화면을 덮지 않게).
			opSeq: 0,
			canEdit: false,
			// DE: 공용 Weekly 인가, 내 고객 링크를 새로 낼 수 있는가, 공용 지정을 바꿀 수 있는가(관리자).
			shared: false,
			canIssue: true,
			canShare: false,
			sharing: false,
			aiEnabled: false,
			tagCodes: [],
			themes: REPORT_THEMES,
			// 서버에 저장돼 있는 지면. 화면에서 고른 값(report.theme)과 달라지면 미리보기가
			// 주소에 실어 보여 준다 — 저장된 값으로만 열면 방금 고른 것을 확인할 수 없다.
			savedTheme: DEFAULT_THEME,
			// 마지막으로 서버에서 읽은 값. 저장할 때 같이 보내면 서버가 "이 화면이 바꾼 칸" 만 적용한다 — 다른 탭(또는
			// 작성자와 어드민)이 그사이 끈 특징 노출이나 지운 AI 문구를 이 화면의 옛 값으로 되살리지 않는다.
			seen: null,
			// 마지막으로 보낸 상세 조회 번호. 다른 리포트로 옮긴 뒤 늦게 온 앞 리포트의 응답(내용·수정 권한)이
			// 지금 화면을 덮지 않게 한다(Codex 리뷰).
			loadSeq: 0,
			report: {},
			items: [],
			links: [],
			itemStats: [],
			botExcluded: 0,
			linkLabel: '',
			linkDays: 90,
			// 고객 링크의 범위(CR). allLinks 는 다른 담당자의 링크가 보이는가(master 만, DE),
			// linkLevel 은 그 범위(self·all), memFilter 는 고른 담당자(null = 범위 전체).
			allLinks: false,
			linkLevel: 'self',
			staff: [],
			memFilter: null,
			me: 0,
			otherLinks: 0,
			myLinks: [],
			statsSeq: 0,
		};
	},
	computed: {
		/** 화면의 매물 순서가 읽어 온 순서와 다른가. 빼기로 줄어든 매물은 순서 변경이 아니다(남은 매물의 상대 순서만 본다). */
		orderChanged() {
			const was = this.seen && this.seen.order;
			if (!was) return false;
			const now = this.items.map((it) => it.item_uid);
			const kept = was.filter((id) => now.includes(id));
			return now.some((id, i) => id !== kept[i]);
		},
		/** 스냅샷에 좌표(pt)가 없는 매물 수. 항공사진 표지의 지도는 이 값으로 그린다. */
		noSpotCnt() {
			return this.items.filter((it) => {
				const pt = (it.snapshot || {}).pt;
				return !pt || !Number.isFinite(Number(pt.x)) || !Number.isFinite(Number(pt.y));
			}).length;
		},
		reportUid() {
			// '123abc'·음수·안전 정수 밖은 0 으로 — parseInt 는 앞자리만 읽어 엉뚱한 리포트를 가리킨다(Codex 리뷰).
			const raw = String(this.$route.params.id || '');
			if (!/^\d{1,15}$/.test(raw)) return 0;
			const n = Number(raw);
			return Number.isSafeInteger(n) && n > 0 ? n : 0;
		},
		maxVisibleMs() {
			return this.itemStats.reduce((m, s) => Math.max(m, Number(s.visible_ms) || 0), 0);
		},
		staffTotal() {
			return this.staff.reduce((a, s) => a + (Number(s.link_cnt) || 0), 0);
		},
		memName() {
			const s = this.staff.find((x) => Number(x.mem_uid) === Number(this.memFilter));
			return s ? s.name : '';
		},
		/** 관심도 패널이 누구의 고객을 합친 것인지. 링크 표와 같은 범위다. */
		interestScope() {
			if (!this.allLinks) return '내 고객 기준';
			if (this.memFilter) return `${this.memName}님 고객 기준`;
			return '전체 고객 기준';
		},
	},
	watch: {
		// /ReportEdit/A → /ReportEdit/B 는 컴포넌트를 재사용한다. 상태를 비우고 다시 읽지 않으면
		// 화면은 A 인데 저장은 B 로 간다(Codex 리뷰).
		reportUid() {
			this.report = {}; this.items = []; this.links = []; this.itemStats = []; this.loaded = false;
			this.staff = []; this.memFilter = null; this.otherLinks = 0; this.myLinks = [];
			this.seen = null; this.busy = false;
			this.opSeq += 1;      // 앞 리포트에서 진행 중이던 작업은 이제 이 화면을 건드리지 않는다
			this.statsSeq += 1;   // 앞 리포트의 통계 응답을 버린다
			this.load();
		},
	},
	created() {
		this.load();
	},
	methods: {
		ymd(v) { return String(v || '').slice(0, 10); },
		dt(v) { return v ? String(v).replace('T', ' ').slice(0, 16) : '-'; },
		won(v) { return v != null && v !== '' ? formatMoneyMuch(v) : '-'; },
		/** ms → "3분 20초" — 초 단위 숫자는 담당자에게 의미가 없다. */
		dur(ms) {
			const s = Math.round((Number(ms) || 0) / 1000);
			if (!s) return '-';
			if (s < 60) return `${s}초`;
			const m = Math.floor(s / 60);
			const r = s % 60;
			return r ? `${m}분 ${r}초` : `${m}분`;
		},
		pct(ms) {
			if (!this.maxVisibleMs) return 0;
			return Math.round((Number(ms) || 0) / this.maxVisibleMs * 100);
		},
		/** 관심도 패널의 이름 아래 한 줄. 본 사람이 없으면 숫자 셋 대신 그 사실을 적는다. */
		interestSub(s) {
			if (!Number(s.viewer_cnt) && !Number(s.visible_ms)) return '아직 본 사람이 없습니다';
			return `체류 ${this.dur(s.visible_ms)} · ${s.viewer_cnt}명 · 사진 확대 ${Number(s.photo_cnt) || 0}회`;
		},
		thumb(it) {
			const ids = (it.snapshot && it.snapshot.img_ids) || [];
			return ids.length ? `${process.env.VUE_APP_HOST_BACK}/users/item/image?id=${ids[0]}&w=240&q=70` : '';
		},
		nameOf(bid) {
			const it = this.items.find((x) => Number(x.building_uid) === Number(bid));
			return it ? (it.snapshot.building_name || `매물 ${bid}`) : `매물 ${bid}`;
		},
		expired(l) {
			// 서버가 DB 시각으로 계산한 값을 쓴다. 'YYYY-MM-DD HH:mm' 을 new Date() 로 다시 읽으면
			// 브라우저·PC 시간대에 따라 결과가 갈린다(Safari 는 아예 Invalid Date)(Codex 리뷰).
			return Number(l.is_expired) === 1;
		},

		/** 리포트를 다시 읽는다. 끝날 때까지 기다릴 수 있게 Promise 를 돌려준다(작업 잠금이 다시 읽기까지 이어진다). */
		load() {
			if (!this.reportUid) return Promise.resolve();
			const uid = this.reportUid;
			const seq = ++this.loadSeq;
			return this.$apiGET(`/admin/api/report/item?uid=${uid}`).then((re) => {
				if (seq !== this.loadSeq || uid !== this.reportUid) return;   // 늦게 온 앞 요청의 응답
				if (!re || !re.report) {
					// 빈 화면으로 조용히 있지 않는다 — 사유를 알리고 목록으로 보낸다.
					alert((re && re.msg) || '리포트를 불러오지 못했습니다. 삭제됐거나 권한이 없을 수 있습니다.');
					// 저장 뒤 다시 읽기가 실패한 경우에도 옛 화면을 남겨 두지 않는다. 남겨 두면 목록으로 넘어가는 사이(게이트
					// 확인을 기다린다) 잠금이 풀린 입력칸에 쓴 글이 이동과 함께 사라진다(Codex 리뷰).
					this.loaded = false;
					return this.$router.replace({ name: 'ReportAdmin' }).catch(() => {});
				}
				this.report = re.report;
				// 모르는 값(옛 리포트·손으로 고친 DB)은 도록으로 접는다 — 서버도 같은 판정을 한다.
				this.report.theme = themeOf(this.report.theme).id;
				this.savedTheme = this.report.theme;
				// show_features 는 서버가 불리언으로 주지만, 문자열 'N' 이 오더라도 켜지지 않게(fail-closed) 여기서도 굳힌다.
				this.items = (re.items || []).map((it) => ({ ...it, show_features: it.show_features === true || it.show_features === 'Y' }));
				this.tagCodes = re.tagCodes || [];
				this.aiEnabled = !!re.aiEnabled;
				this.canEdit = !!re.canEdit;
				this.shared = !!re.shared;
				this.canIssue = re.canIssue !== false;
				this.canShare = !!re.canShare;
				this.allLinks = !!re.allLinks;
				this.linkLevel = re.linkLevel || (re.allLinks ? 'all' : 'self');
				this.seen = this.snapshotSeen();
				this.loaded = true;
				this.loadStats();
			});
		},
		/** 수록 매물 순서를 한 칸 옮긴다. 저장을 눌러야 반영된다(update 가 화면 순서를 sortNo 로 보낸다). */
		moveItem(idx, dir) {
			const to = idx + dir;
			if (this.busy || to < 0 || to >= this.items.length) return;
			const next = this.items.slice();
			[next[idx], next[to]] = [next[to], next[idx]];
			this.items = next;
		},
		/**
		 * 매물을 리포트에서 뺀다. 서버가 곧바로 행을 지우므로, 다른 매물에 적어 둔 저장 전 문구를 잃지
		 * 않도록 화면을 다시 읽지 않고 그 한 건만 목록에서 걷어낸다.
		 */
		removeItem(it) {
			const name = (it.snapshot && it.snapshot.building_name) || `매물 ${it.building_uid}`;
			const warn = this.report.status === 'published' ? '\n발행된 리포트라 보낸 고객 링크에서도 바로 사라집니다.' : '';
			if (!confirm(`'${name}' 을(를) 이 리포트에서 뺍니다.\n이 매물에 적어 둔 코멘트와 태그도 함께 지워집니다.${warn}\n뺄까요?`)) return;
			const op = this.beginOp();
			this.$apiPOST('/admin/api/report/item/remove', {
				reportUid: op.uid, bid: it.building_uid, buildingType: it.building_type || 'sell',
			}).then((re) => {
				if (!this.isCurrent(op) || !re || !re.ok) return;
				this.items = this.items.filter((x) => x.item_uid !== it.item_uid);
				this.loadStats();
			}).catch(() => {}).finally(() => this.endOp(op));
		},
		loadStats() {
			// 담당자 필터는 어드민에게만 의미가 있다. 직원이 mem 을 붙여도 서버는 읽지 않는다.
			const memAt = this.allLinks ? this.memFilter : null;
			const mem = memAt ? `&mem=${memAt}` : '';
			// 칩을 연달아 누르면 늦게 온 앞 응답이 뒤 선택을 덮는다(B 를 골랐는데 A 의 고객이 보인다).
			// 마지막으로 보낸 요청의 응답만 받는다(Codex 리뷰).
			const seq = ++this.statsSeq;
			this.$apiGET(`/admin/api/report/stats?uid=${this.reportUid}${mem}`).then((re) => {
				if (!re || seq !== this.statsSeq) return;
				if (re.scope) {
					this.allLinks = !!re.scope.all;
					this.linkLevel = re.scope.level || (re.scope.all ? 'all' : 'self');
					this.me = Number(re.scope.me) || 0;
					// 서버가 고른 담당자를 받지 않았으면(그 사람이 다른 팀으로 갔다 등) 화면의 선택도 푼다(DC, codex 리뷰).
					if (memAt && !re.scope.mem) this.memFilter = null;
				}
				this.links = re.links || [];
				this.itemStats = re.items || [];
				this.botExcluded = re.botExcluded || 0;
				this.staff = re.staff || [];
				this.otherLinks = Number(re.otherLinks) || 0;
				// 미리보기용 '내 링크'. 어드민이 다른 담당자를 걸러 보는 동안에는 표에 내 링크가 없으므로,
				// 내 링크가 담긴 응답(전체 또는 나로 거른 것)을 받을 때만 갈아 둔다.
				if (!memAt || Number(memAt) === this.me) this.myLinks = this.links.filter((l) => l.mine);
			});
		},
		pickStaff(uid) {
			this.memFilter = uid;
			this.loadStats();
		},
		/**
		 * 공용 지정·해제(관리자 전용, DE). 체크하는 순간 바로 저장된다(저장 버튼과 따로다. 내용이 아니라 공유 범위다).
		 * 리포트를 다시 읽지 않는다: 저장하지 않은 편집이 덮인다. 관리자의 수정 권한은 공용 여부와 상관없이 그대로다.
		 */
		toggleShare(ev) {
			const on = !!ev.target.checked;
			const msg = on
				? '이 Weekly 를 공용으로 지정합니다.\nWeekly 권한이 있는 모든 직원의 목록에 보이고, 각자 자기 고객에게 링크를 발급할 수 있습니다.'
				: '공용 지정을 풉니다.\n이 Weekly 로 링크를 보낸 적이 없는 직원의 목록에서 빠지고, 누구도 새 링크를 발급할 수 없게 됩니다.\n이미 보낸 고객 링크는 그대로 열리고, 보낸 직원은 자기 링크의 열람과 회수만 합니다.';
			if (!confirm(msg)) { ev.target.checked = this.shared; return; }
			const uid = this.reportUid;
			this.sharing = true;
			this.$apiPOST('/admin/api/report/share', { reportUid: uid, shared: on ? 'Y' : 'N' }).then((re) => {
				if (uid !== this.reportUid) return;
				if (re && re.ok) this.shared = !!re.shared;
				else ev.target.checked = this.shared;
			}).catch(() => { ev.target.checked = this.shared; }).finally(() => { this.sharing = false; });
		},

		/** 서버에서 읽은 그대로의 값(저장 때 *Seen 으로 보낸다). */
		snapshotSeen() {
			const items = {};
			for (const it of this.items) {
				items[it.item_uid] = {
					commentFinal: it.comment_final || '',
					showFeatures: it.show_features === true ? 'Y' : 'N',
					tags: Array.isArray(it.tags) ? it.tags.slice() : it.tags,
					featuresHash: it.features_hash,
				};
			}
			return {
				title: this.report.title || '',
				briefFinal: this.report.brief_final || '',
				theme: this.report.theme || DEFAULT_THEME,
				status: this.report.status,
				order: this.items.map((it) => it.item_uid),
				items,
			};
		},
		/** 서버에서 읽은 뒤 이 화면에서 바꾼 것이 있는가(저장하지 않은 편집). */
		isDirty() {
			const seen = this.seen;
			if (!seen) return false;
			if (this.orderChanged) return true;
			if ((this.report.title || '') !== seen.title || (this.report.brief_final || '') !== seen.briefFinal) return true;
			if ((this.report.theme || DEFAULT_THEME) !== seen.theme || this.report.status !== seen.status) return true;
			const tagKey = (v) => (Array.isArray(v) ? v.slice().sort().join(',') : String(v || ''));
			return this.items.some((it) => {
				const was = seen.items[it.item_uid];
				if (!was) return false;
				return (it.comment_final || '') !== was.commentFinal
					|| (it.show_features === true ? 'Y' : 'N') !== was.showFeatures
					|| tagKey(it.tags) !== tagKey(was.tags);
			});
		},
		/** 작업을 시작한다: 입력을 잠그고 작업 번호를 받는다. */
		beginOp() {
			this.busy = true;
			this.opSeq += 1;
			return { seq: this.opSeq, uid: this.reportUid };
		},
		/** 이 작업이 아직 지금 화면의 작업인가(그사이 다른 작업이 시작되거나 리포트를 옮기지 않았는가). */
		isCurrent(op) {
			return op.seq === this.opSeq && op.uid === this.reportUid;
		},
		/** 작업을 끝낸다. 지금 작업일 때만 잠금을 푼다. */
		endOp(op) {
			if (op.seq === this.opSeq) this.busy = false;
		},
		/** 저장 충돌이면 안내하고, 원하면 서버 값으로 다시 읽는다(다시 읽는 동안에도 잠금이 이어진다). 충돌이었으면 true. */
		async handleConflict(re) {
			if (!(re && re.conflict)) return false;
			if (confirm(`${re.msg || '다른 사람이 먼저 저장했습니다.'}\n\n지금 저장된 내용으로 화면을 다시 불러올까요?\n(이 화면에서 고친 내용은 사라지니 필요하면 먼저 복사해 두세요.)`)) await this.load();
			return true;
		},
		/**
		 * 저장 응답을 사람에게 알릴 문장으로 바꾼다. 실패·충돌이면 null.
		 * 저장 버튼과 'AI 문구 생성' 의 사전 저장이 같이 쓴다 — 사전 저장에서 지운 문구를 알리지 않으면, 이어지는
		 * 생성이 실패했을 때 화면에는 서버에서 이미 지운 문구가 남는다(Codex 리뷰).
		 */
		saveNotes(re) {
			if (!re || re.conflict || !re.ok) return null;
			const lines = [];
			if (Number(re.aiCleared) > 0) lines.push(`특징 노출을 끈 매물 ${re.aiCleared}건의 AI 코멘트는 사내 메모로 만든 문장이라 함께 지웠습니다.`);
			if (re.briefCleared) lines.push('특징 노출을 끈 매물의 메모로 만든 Weekly Brief 초안도 함께 지웠습니다.');
			if (Number(re.featuresStale) > 0) lines.push(`특징 문장이 그사이 바뀐 매물 ${re.featuresStale}건은 고객 노출을 켜지 않았습니다. 바뀐 문장을 확인한 뒤 다시 켜 주세요.`);
			return lines;
		},
		/** 저장 전 확인. 빈 제목을 보내면 서버는 제목을 그대로 두는데 화면은 저장됐다고 알렸다(Codex 리뷰). */
		canSave() {
			if (!String(this.report.title || '').trim()) { alert('제목을 입력하세요.'); return false; }
			return true;
		},
		updatePayload() {
			const seen = this.seen || { items: {} };
			return {
				reportUid: this.reportUid,
				title: this.report.title,
				titleSeen: seen.title,
				briefFinal: this.report.brief_final || '',
				briefFinalSeen: seen.briefFinal,
				// 화면이 읽어 둔 AI 초안도 함께 보낸다 — 서버가 "담당자가 손대지 않은 문구" 를 판별하는 근거(다른 탭에서
				// 재생성이 먼저 저장돼도 이 화면 기준으로 판단할 수 있다).
				briefAi: this.report.brief_ai || '',
				status: this.report.status,
				statusSeen: seen.status,
				theme: this.report.theme || DEFAULT_THEME,
				themeSeen: seen.theme,
				items: this.items.map((it, i) => {
					const was = seen.items[it.item_uid] || {};
					return {
						itemUid: it.item_uid,
						commentFinal: it.comment_final || '',
						commentFinalSeen: was.commentFinal,
						commentAi: it.comment_ai || '',
						tags: it.tags,
						tagsSeen: was.tags,
						showFeatures: it.show_features === true ? 'Y' : 'N',
						showFeaturesSeen: was.showFeatures,
						// 켤 때 화면이 본 특징 문장의 지문 — 그사이 새로고침으로 문장이 바뀌었으면 서버가 켜지 않는다.
						featuresHashSeen: was.featuresHash,
						// 순서를 바꾸지 않았으면 보내지 않는다 — 다른 탭이 바꾼 순서를 옛 순서로 되돌리지 않게.
						sortNo: this.orderChanged ? i : undefined,
					};
				}),
			};
		},
		// 저장·생성·새로고침은 끝나면 화면을 다시 읽는다. **다시 읽기가 끝날 때까지 잠금을 유지한다** — 먼저 풀면 그사이
		// 고친 글을 다시 읽은 값이 덮고, 이 화면의 Seen 도 옛 값으로 남아 다음 저장이 자기 저장과 충돌하거나 노출 끄기를
		// 무시한다(Codex 리뷰). 잠금은 작업 번호로 푼다(beginOp·endOp).
		async save() {
			if (this.busy || !this.canSave()) return;
			const op = this.beginOp();
			const sent = this.report.theme || DEFAULT_THEME;
			try {
				const re = await this.$apiPOST('/admin/api/report/update', this.updatePayload());
				if (!this.isCurrent(op)) return;   // 저장하는 사이 다른 리포트로 옮겼다
				if (await this.handleConflict(re)) return;
				const notes = this.saveNotes(re);
				if (!notes) return;
				this.savedTheme = sent;
				if (notes.length) {
					// 특징 노출을 끈 매물의 AI 코멘트(와 손대지 않은 Weekly Brief)는 그 특징(사내 메모)으로 만든 문장이라 서버가 함께 지운다.
					if (Number(re.aiCleared) > 0 || re.briefCleared) notes.push('문구를 다시 생성하거나 직접 입력해 주세요.');
					alert(['저장했습니다.', ...notes].join('\n\n'));
				} else {
					alert('저장했습니다.');
				}
				// 이 화면이 바꾸지 않은 칸은 서버의 값이 남는다(그사이 다른 탭이 바꿨을 수 있다) → 화면을 서버에 맞춘다.
				if (this.isCurrent(op)) await this.load();
			} catch (e) {
				// apiPOST 는 실패를 false 로 돌려준다. 그 밖의 예외도 잠금만 푼다.
			} finally {
				this.endOp(op);
			}
		},

		async genAi() {
			// 화면의 특징 노출·문구를 **먼저 저장**한다. 저장 전에 생성하면 서버는 DB 의 옛 상태로
			// 재료를 고르므로, 방금 끈 매물의 사내 메모가 AI 로 넘어간다(Codex 리뷰).
			// 생성하는 동안 입력칸은 잠긴다 — 끝나면 다시 읽어 오므로 그사이 쓴 글이 사라진다(Codex 리뷰).
			if (this.busy || !this.canSave()) return;
			const op = this.beginOp();   // 생성 중에 다른 리포트로 옮겨도 이 리포트에 대해서만 요청한다
			try {
				const saved = await this.$apiPOST('/admin/api/report/update', this.updatePayload());
				if (!this.isCurrent(op)) return;
				if (await this.handleConflict(saved)) return;
				const preNotes = this.saveNotes(saved);
				if (!preNotes) return;
				const re = await this.$apiPOST('/admin/api/report/ai', { reportUid: op.uid });
				if (!this.isCurrent(op)) return;
				const head = !re ? null : (re.ok ? (re.msg || '문구를 생성했습니다.') : (re.msg || 'AI 문구를 만들지 못했습니다.'));
				const lines = [head, ...preNotes].filter(Boolean);
				if (lines.length) alert(lines.join('\n\n'));
				// 사전 저장이 성공했으므로 생성 결과와 무관하게 다시 읽는다(초안, 사전 저장에서 지운 문구, 새 Seen).
				if (this.isCurrent(op)) await this.load();
			} catch (e) {
				// 잠금만 푼다
			} finally {
				this.endOp(op);
			}
		},

		async refreshItems() {
			if (this.busy) return;
			// 새로고침은 끝나면 화면을 다시 읽는다 — 저장하지 않은 편집이 있으면 사라진다(Codex 리뷰).
			if (this.isDirty()) { alert('저장하지 않은 변경이 있습니다. 먼저 저장한 뒤 매물정보를 새로고침해 주세요.'); return; }
			if (!confirm('매물의 현재 가격·면적 등을 다시 불러옵니다.\n이미 고객에게 보낸 링크의 내용도 함께 바뀝니다. 계속할까요?')) return;
			const op = this.beginOp();
			try {
				const re = await this.$apiPOST('/admin/api/report/refresh', { reportUid: op.uid });
				if (!this.isCurrent(op) || !re || !re.ok) return;
				let msg = `매물 ${re.updated}건을 최신 정보로 갱신했습니다.`;
				if (Number(re.featuresReset) > 0) {
					// 메모가 바뀐 매물은 새 문장이 승인된 적이 없으므로 서버가 노출을 끈다. 담당자가 다시 본다.
					msg += `\n\n매물 특징이 바뀐 ${re.featuresReset}건은 고객 노출을 껐습니다. 내용을 확인한 뒤 다시 켜 주세요.`;
				}
				if (Number(re.aiCleared) > 0 || re.briefCleared) {
					msg += '\n\n노출을 끈 매물의 옛 메모로 만든 AI 문구(손대지 않은 것)도 함께 지웠습니다. 다시 생성하거나 직접 입력해 주세요.';
				}
				alert(msg);
				if (this.isCurrent(op)) await this.load();
			} catch (e) {
				// 잠금만 푼다
			} finally {
				this.endOp(op);
			}
		},

		addLink() {
			if (this.busy) return;   // Enter 연타로 같은 고객 링크가 두 번 발급되는 것을 막는다(Codex 리뷰)
			const label = String(this.linkLabel || '').trim();
			if (!label) { alert('고객명을 입력하세요.'); return; }
			const op = this.beginOp();
			this.$apiPOST('/admin/api/report/link/add', {
				reportUid: op.uid, label, days: this.linkDays,
			}).then((re) => {
				if (!this.isCurrent(op) || !re || !re.ok) return;
				this.linkLabel = '';
				// 어드민이 다른 담당자를 걸러 보던 중이면 방금 만든(내) 링크가 표에 안 보인다 → 전체로 돌린다.
				if (this.allLinks && this.memFilter && Number(this.memFilter) !== this.me) this.memFilter = null;
				this.loadStats();
				this.doCopy(this.urlOf(re.token), `${this.honor(label)} 링크를 복사했습니다.`);
			}).catch(() => {}).finally(() => this.endOp(op));
		},

		toggleLink(l) {
			const restore = !!l.revoked_at;
			if (!restore && !confirm('링크를 회수하면 고객이 더 이상 열 수 없습니다. 계속할까요?')) return;
			this.$apiPOST('/admin/api/report/link/revoke', { linkUid: l.link_uid, restore }).then((re) => {
				if (re && re.ok) this.loadStats();
			});
		},

		/** 담당자가 '김대표님' 처럼 존칭을 붙여 적는 경우가 많아 '님' 을 겹쳐 붙이지 않는다. */
		honor(label) {
			const s = String(label || '').trim();
			return /님$/.test(s) ? s : `${s} 님`;
		},
		urlOf(token) {
			return `${window.location.origin}/r/${token}`;
		},
		copyLink(l) {
			this.doCopy(this.urlOf(l.token), `${this.honor(l.client_label)} 링크를 복사했습니다.`);
		},

		/**
		 * 클립보드 복사.
		 * navigator.clipboard 는 **보안 컨텍스트(https)에서만** 동작한다. 운영은 http 라
		 * 그것만 쓰면 복사가 조용히 실패한다 — execCommand 폴백이 실제 동작 경로다.
		 */
		doCopy(text, okMsg) {
			const done = () => alert(`${okMsg}\n\n${text}`);
			try {
				if (navigator.clipboard && window.isSecureContext) {
					navigator.clipboard.writeText(text).then(done).catch(() => this.legacyCopy(text, done));
					return;
				}
			} catch (e) { /* 폴백으로 */ }
			this.legacyCopy(text, done);
		},
		legacyCopy(text, done) {
			try {
				const ta = document.createElement('textarea');
				ta.value = text;
				ta.setAttribute('readonly', '');
				ta.style.position = 'fixed';
				ta.style.opacity = '0';
				document.body.appendChild(ta);
				ta.select();
				const ok = document.execCommand('copy');
				document.body.removeChild(ta);
				if (!ok) { alert(`자동 복사가 되지 않는 환경입니다. 아래 주소를 직접 복사해 주세요.\n\n${text}`); return; }
			} catch (e) {
				alert(`자동 복사가 되지 않는 환경입니다. 아래 주소를 직접 복사해 주세요.\n\n${text}`);
				return;
			}
			done();
		},

		preview() {
			const usable = (x) => x.token && !x.revoked_at && !this.expired(x);
			// 내 링크를 먼저 쓴다. 미리보기도 그 링크의 열람으로 남을 수 있어서, 다른 담당자 고객의 링크로
			// 열면 그 담당자가 보는 고객 리드에 없는 관심이 쌓인다(CR). 어드민만 남의 링크까지 보이므로
			// 내 링크가 없을 때는 누구의 어느 고객 링크로 여는지 알리고 묻는다.
			// 표가 아니라 myLinks 에서 찾는다 — 어드민이 다른 담당자로 걸러 보는 중이면 표에는 내 링크가 없다(Codex 리뷰).
			let l = this.myLinks.find(usable);
			if (!l && this.allLinks) {
				const other = this.links.find(usable);
				if (other && confirm(`내가 발급한 링크가 없어 ${other.created_name}님의 고객 '${other.client_label}' 링크로 엽니다.\n이 화면을 보는 동안의 체류가 그 고객의 열람으로 기록될 수 있습니다. 계속할까요?`)) l = other;
				else if (other) return;
			}
			if (!l) {
				alert(this.canIssue ? '먼저 내 고객 링크를 하나 발급해 주세요. 그 링크가 곧 고객이 보는 화면입니다.'
					: '열 수 있는 내 고객 링크가 없습니다. 공용이 해제된 Weekly 라 새 링크도 발급할 수 없습니다.');
				return;
			}
			// 아직 저장하지 않은 지면을 고른 상태면 주소에 실어 **그 지면으로** 보여 준다.
			// 저장된 값으로만 열면 담당자는 방금 고른 것을 확인할 방법이 없다.
			const picked = this.report.theme || DEFAULT_THEME;
			const q = picked !== themeOf(this.savedTheme).id ? `?theme=${encodeURIComponent(picked)}` : '';
			// noopener 로 열면 새 탭에 로그인(sessionStorage)이 넘어가지 않아, 고객 화면이 담당자를 알아보지 못하고
			// 이 미리보기를 고객의 열람으로 셌다(2026-10-12 운영 실측). 그냥 열어 로그인을 복사해 넘기고(같은 출처라
			// 브라우저가 복사한다) opener 만 끊는다.
			const w = window.open(this.urlOf(l.token) + q, '_blank');
			if (w) { try { w.opener = null; } catch (e) { /* 이미 닫힘 */ } }
		},

		async removeReport() {
			// 리포트를 지우면 **다른 담당자가 자기 고객에게 보낸 링크도 함께 닫힌다**(CR). 작성자는 그 링크를
			// 볼 수 없으니 수라도 알려 준다(서버가 수만 준다). 전체 범위(어드민)는 받은 링크에서 센다.
			// 화면에 이미 받아 둔 값이 아니라 **지금** 센다 — 통계가 오기 전에 누르면 0 으로 보여 경고가 빠진다(Codex 리뷰).
			// 지울 리포트 번호는 누른 순간에 고정한다. 통계를 기다리는 사이 다른 리포트로 옮기면 그쪽이 지워졌다(Codex 리뷰).
			const uid = this.reportUid;
			const re = await this.$apiGET(`/admin/api/report/stats?uid=${uid}`);
			if (!re || uid !== this.reportUid) return;
			const all = !!(re.scope && re.scope.all);
			const others = all
				? (re.links || []).filter((l) => !l.mine && !l.revoked_at).length
				: Number(re.otherLinks) || 0;
			// 직원은 다른 담당자의 링크가 살아 있는 리포트를 지우지 못한다(DE, 서버도 막는다). 관리자만 경고를 보고 지운다.
			if (!all && others) {
				alert(`다른 직원이 이 Weekly 로 고객에게 보낸 링크가 ${others}개 있어 관리자만 삭제할 수 있습니다. 관리자에게 요청해 주세요.`);
				return;
			}
			const warn = others ? `\n\n다른 담당자가 이 리포트로 고객에게 보낸 링크 ${others}개도 함께 열리지 않게 됩니다.` : '';
			if (!confirm(`리포트를 삭제하면 발급된 링크도 모두 열리지 않습니다.${warn}\n\n계속할까요?`)) return;
			this.$apiPOST('/admin/api/report/delete', { reportUid: uid }).then((re) => {
				if (re && re.ok) this.$router.replace({ name: 'ReportAdmin' });
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/design-tokens.scss";

/* 공용 Weekly 지정(관리자). 체크 하나와 설명 한 줄이다. */
.re-share {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-weight: 600;
	white-space: nowrap;
	cursor: pointer;
	input { margin: 0; }
}

/* ── 공통 조각 ─────────────────────────────────────────────────────────────
   이 화면은 콘솔이다(DESIGN.md): 흰 섹션, 라벨칸 + 값칸 입력 표, 회색 머리칸 표, 파랑은
   "누르면 무슨 일이 일어난다" 에만. 고객 문서의 버건디·크림은 여기 오지 않는다. */
.re-readonly {
	@include dt-type-dense;
	background: #fff8e6;
	border: 1px solid #f0dfae;
	border-radius: $dt-rounded-control;
	padding: 8px 12px;
	margin: 0 0 14px;
}
.re-col-label { width: 128px; }
.re-form {
	td { vertical-align: top; }
	th { vertical-align: top; padding-top: 12px; }
}
.re-row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}
.re-title { flex: 1 1 320px; max-width: 640px; }
.re-status { width: 96px; flex: 0 0 auto; height: 28px; padding-top: 2px; padding-bottom: 2px; font-size: 13px; }
.re-hint {
	@include dt-type-dense;
	font-size: 12px;
	color: $dt-color-ink-faint;
	&--row { margin-top: 8px; }
	&--block { margin: 0 0 12px; }
	&--warn { color: $dt-color-warning-ink; margin-top: 6px; }
	b { color: $dt-color-ink-muted; }
}
.re-muted { color: $dt-color-ink-faint; }
.re-ai {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 8px;
}
.re-draft {
	@include dt-type-dense;
	font-size: 12px;
	color: $dt-color-ink-muted;
	background: $dt-color-surface-sunken;
	border-left: 3px solid $dt-color-line-strong;
	padding: 6px 10px;
	margin-top: 6px;
	&__label {
		font-weight: 600;
		color: $dt-color-ink-faint;
		margin-right: 8px;
	}
}
.re-link {
	border: 0;
	background: none;
	color: $dt-color-primary;
	text-decoration: underline;
	padding: 0 0 0 8px;
	cursor: pointer;
	font-size: 12px;
	&--cell { padding: 0; font-size: 13px; }
}
.table td.left { text-align: left; }
.table td.num { font-variant-numeric: tabular-nums; }
.table td.zero { color: $dt-color-ink-faint; }
.re-empty {
	padding: 22px 8px !important;
	color: $dt-color-ink-faint;
}
/* 상태는 배지가 아니라 잉크색 한 단어(표 안 인라인에 배경을 칠하지 않는다). */
.re-state {
	font-weight: 600;
	&.is-on { color: $dt-color-success-ink; }
	&.is-off { color: $dt-color-ink-faint; }
}
.table tr.is-off td { color: $dt-color-ink-faint; }
.table tr.is-off td b { font-weight: 400; }

/* ── 지면 고르기 ─────────────────────────────────────────────────────────── */
.th-list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: grid;
	/* 1400px 콘솔에서 일곱 지면이 한 줄에 선다(칸 ~172px). 더 좁으면 자연히 접힌다. */
	grid-template-columns: repeat(auto-fill, minmax(164px, 1fr));
	gap: 8px;
}
.th-opt {
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	padding: 8px 10px;
	background: $dt-color-surface;
	border: 1px solid $dt-color-line-strong;
	border-radius: $dt-rounded-control;
	cursor: pointer;
	text-align: left;
	&:hover:not(:disabled) { background: $dt-color-surface-sunken; }
	&.on {
		border-color: $dt-color-primary;
		box-shadow: inset 0 0 0 1px $dt-color-primary;
		background: #f0f7ff;
	}
	&:disabled { cursor: default; opacity: 0.7; }
}
.th-txt {
	min-width: 0;
	strong { display: block; font-size: 13px; font-weight: 600; line-height: 1.4; }
	span {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		font-size: 11.5px;
		color: $dt-color-ink-faint;
		line-height: 1.4;
	}
}
/* 표지 골격을 54x76 안에 그린다. 세 색 띠로는 "어느 지면" 이 안 읽혔다. 값과 구성은
   frontend/src/utils/reportThemes.js 와 고객 화면의 표지 골격을 손으로 옮긴 것이다. */
.th-cover {
	flex: 0 0 auto;
	position: relative;
	width: 54px;
	height: 76px;
	border: 1px solid $dt-color-line-strong;
	overflow: hidden;
	background: #fff;
	i { display: none; position: absolute; }
}
/* ① 모노그램: 크림 지면, 오른쪽 위 재단선 밖으로 나가는 버건디 원, 왼쪽 아래 활자 덩어리 */
.th-cover--catalog {
	background: #f7f2e8;
	i:nth-child(1) { display: block; width: 46px; height: 46px; border-radius: 50%; border: 7px solid #8f2b32; right: -16px; top: -12px; }
	i:nth-child(2) { display: block; left: 7px; bottom: 22px; width: 12px; height: 2px; background: #a8823f; }
	i:nth-child(3) { display: block; left: 7px; bottom: 14px; width: 30px; height: 4px; background: #241d1d; }
	i:nth-child(4) { display: block; left: 7px; bottom: 9px; width: 18px; height: 2px; background: #5d5551; }
}
/* ② 금선 도판: 먹지, 금 괘선을 두른 판, 아래 제목 */
.th-cover--noir {
	background: #15130f;
	i:nth-child(1) { display: block; left: 8px; top: 12px; width: 38px; height: 28px; border: 1px solid #c9a253; background: #3a3228; }
	i:nth-child(2) { display: block; left: 8px; bottom: 18px; width: 30px; height: 4px; background: #f2ece0; }
	i:nth-child(3) { display: block; left: 8px; bottom: 11px; width: 16px; height: 2px; background: #b3a692; }
}
/* ③ 항공사진 전면: 도시 위성사진 위에 번호 배지, 아래 스크림과 활자 */
.th-cover--aerial {
	background: linear-gradient(165deg, #4a6b7e 0%, #2b4657 45%, #16202a 100%);
	i:nth-child(1) { display: block; left: 12px; top: 14px; width: 7px; height: 7px; border-radius: 50%; background: #0f6b8c; border: 1px solid #fff; }
	i:nth-child(2) { display: block; left: 30px; top: 24px; width: 7px; height: 7px; border-radius: 50%; background: #0f6b8c; border: 1px solid #fff; }
	i:nth-child(3) { display: block; left: 20px; top: 38px; width: 7px; height: 7px; border-radius: 50%; background: #0f6b8c; border: 1px solid #fff; }
	i:nth-child(4) { display: block; left: 8px; bottom: 14px; width: 32px; height: 4px; background: #fff; }
	i:nth-child(5) { display: block; left: 8px; bottom: 8px; width: 20px; height: 2px; background: #c9d2d9; }
}
/* ④ 스위스 그리드: 흰 지면, 굵은 먹선, 큰 제목, 주황 강조 */
.th-cover--swiss {
	background: #fff;
	i:nth-child(1) { display: block; left: 6px; right: 6px; top: 9px; height: 2px; background: #111; }
	i:nth-child(2) { display: block; left: 6px; top: 16px; width: 34px; height: 6px; background: #111; }
	i:nth-child(3) { display: block; left: 6px; top: 25px; width: 26px; height: 6px; background: #111; }
	i:nth-child(4) { display: block; left: 6px; right: 6px; top: 40px; height: 1px; background: #d5d5d5; }
	i:nth-child(5) { display: block; left: 6px; right: 6px; top: 48px; height: 1px; background: #d5d5d5; }
	i:nth-child(6) { display: block; left: 6px; bottom: 8px; width: 7px; height: 7px; background: #e8590c; }
}
/* ⑤ 색면 분할: 위 절반 딥그린 색면에 흰 제목, 아래 크림에 목록 */
.th-cover--field {
	background: linear-gradient(#1f4d38 0 54%, #f6f3ea 54%);
	i:nth-child(1) { display: block; left: 7px; top: 24px; width: 30px; height: 5px; background: #f6f3ea; }
	i:nth-child(2) { display: block; left: 7px; top: 32px; width: 18px; height: 2px; background: #cfd8cf; }
	i:nth-child(3) { display: block; left: 7px; bottom: 20px; width: 34px; height: 2px; background: #16241c; }
	i:nth-child(4) { display: block; left: 7px; bottom: 14px; width: 34px; height: 2px; background: #16241c; }
	i:nth-child(5) { display: block; left: 7px; bottom: 8px; width: 24px; height: 2px; background: #16241c; }
}
/* ⑥ 갤러리 도판: 미색 여백 한가운데 작은 도판 하나와 캡션 */
.th-cover--gallery {
	background: #f3f0e9;
	i:nth-child(1) { display: block; left: 13px; top: 16px; width: 28px; height: 22px; background: #bfb7a8; }
	i:nth-child(2) { display: block; left: 13px; top: 44px; width: 20px; height: 2px; background: #6b6257; }
	i:nth-child(3) { display: block; left: 13px; top: 49px; width: 12px; height: 1px; background: #b9ad97; }
}
/* ⑦ 사진 격자: 먹지 위 3x3 사진 칸, 한 칸이 테라코타 */
.th-cover--mosaic {
	background: #141414;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-template-rows: repeat(3, 1fr);
	gap: 1px;
	padding: 1px;
	box-sizing: border-box;
	i { display: block; position: static; background: #4a4a4a; }
	i:nth-child(2) { background: #6b6b6b; }
	i:nth-child(3) { background: #c2643a; }
	i:nth-child(5) { background: #141414; }
	i:nth-child(6) { background: #141414; }
	i:nth-child(7) { background: #5c5c5c; }
	i:nth-child(8) { background: #3e3e3e; }
	i:nth-child(9) { background: #141414; }
}

/* ── 수록 매물 ────────────────────────────────────────────────────────────── */
.lot {
	display: flex;
	gap: 16px;
	padding: 16px 0;
	border-top: 1px solid $dt-color-line;
	&:first-of-type { border-top: 0; padding-top: 4px; }
	&__no {
		flex: 0 0 34px;
		font-size: 22px;
		font-weight: 600;
		line-height: 1;
		color: $dt-color-ink-faint;
		font-variant-numeric: tabular-nums;
		padding-top: 2px;
	}
	&__thumb {
		flex: 0 0 132px;
		width: 132px;
		img, .lot__noimg {
			display: block;
			width: 100%;
			height: 92px;
			object-fit: cover;
			border-radius: $dt-rounded-chip;
			background: $dt-color-surface-sunken;
		}
		.lot__noimg {
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 12px;
			color: $dt-color-ink-faint;
		}
	}
	&__main { flex: 1 1 auto; min-width: 0; }
	&__head {
		display: flex;
		align-items: baseline;
		gap: 10px;
		flex-wrap: wrap;
		margin-bottom: 4px;
	}
	&__name { font-weight: 600; font-size: 15px; }
	&__addr { font-size: 12px; color: $dt-color-ink-faint; }
	/* 순서·빼기. 행 오른쪽 끝에 붙여 이름·주소를 읽는 흐름을 끊지 않는다. */
	&__ops {
		margin-left: auto;
		display: inline-flex;
		gap: 4px;
		align-self: center;
	}
	/* 공통 '.section-tit .sub'(회색)보다 앞서야 해서 제목 줄까지 붙여 쓴다. 저장 전 상태라는 경고다. */
	.section-tit &__order-note { color: $dt-color-warning-ink; font-weight: 600; margin-left: 12px; }
	/* 제원은 라벨·값 쌍을 한 줄에 늘어놓는다. 매물마다 같은 자리라 나란히 비교된다. */
	&__spec {
		display: flex;
		flex-wrap: wrap;
		gap: 2px 18px;
		margin: 0 0 8px;
		div { display: flex; gap: 6px; align-items: baseline; }
		dt { font-size: 11.5px; color: $dt-color-ink-faint; font-weight: 400; }
		dd { margin: 0; font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
	}
	&__tags { margin-bottom: 8px; display: flex; flex-wrap: wrap; gap: 4px; }
	/* 갈래 칩. 필터 칩(DESIGN.md filter-chip / filter-chip-on)과 같은 생김새. */
	&__chip {
		display: inline-flex;
		align-items: center;
		height: 24px;
		padding: 0 11px;
		border-radius: $dt-rounded-full;
		background: $dt-color-canvas;
		color: $dt-color-ink-muted;
		@include dt-type-cell-control;
		cursor: pointer;
		user-select: none;
		input { position: absolute; width: 0; height: 0; opacity: 0; }
		&.on { background: $dt-color-primary-ink; color: $dt-color-surface; }
		&.off { cursor: default; }
	}
	&__field {
		display: flex;
		align-items: center;
		gap: 8px;
		.form-control { flex: 1 1 auto; }
	}
	&__label {
		flex: 0 0 auto;
		font-size: 12px;
		font-weight: 600;
		color: $dt-color-ink-muted;
		width: 46px;
	}
	/* 특징(사내 메모). 켜기 전에는 옅게, 켜면 본문 잉크로. 취소선은 "지워진 글" 로 읽혀 뺐다. */
	&__feat {
		margin-top: 8px;
		padding: 8px 12px;
		background: $dt-color-surface-sunken;
		border-radius: $dt-rounded-chip;
		&-toggle {
			font-size: 12.5px;
			font-weight: 600;
			.text { margin-left: 6px; }
		}
		&-state {
			margin-left: 10px;
			font-weight: 400;
			font-size: 12px;
			color: $dt-color-ink-faint;
		}
		&.on &-state { color: $dt-color-success-ink; }
		&-warn {
			font-size: 11.5px;
			color: $dt-color-warning-ink;
			margin: 4px 0 2px;
		}
		&-list {
			margin: 4px 0 0;
			padding-left: 18px;
			font-size: 12.5px;
			line-height: 1.6;
			color: $dt-color-ink-faint;
		}
		&.on &-list { color: $dt-color-ink-muted; }
	}
}

/* ── 고객 링크 ────────────────────────────────────────────────────────────── */
/* 담당자별 칩(어드민). 계약 관리의 직원별 칩(ContractAdmin .ct-staff)과 같은 생김새다. */
.re-staff {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	margin-bottom: 12px;
	max-height: 96px;
	overflow-y: auto;
	&__label {
		font-size: 12px;
		font-weight: 600;
		color: $dt-color-ink-muted;
		margin-right: 2px;
	}
	// bootstrap .btn 과 특이도가 같으면 로드 순서에 따라 배경·테두리가 덮인다 → 부모를 함께 쓴다.
	.re-staff__chip {
		height: 24px;
		padding: 0 10px;
		font-size: 12px;
		line-height: 1;
		border: 1px solid $dt-color-line-strong;
		border-radius: $dt-rounded-full;
		background: $dt-color-canvas;
		color: $dt-color-ink-muted;
		white-space: nowrap;
		&:hover { border-color: $dt-color-ink-faint; color: $dt-color-ink; }
		&.on {
			background: $dt-color-primary-ink;
			border-color: $dt-color-primary-ink;
			color: #fff;
		}
	}
	&__cnt {
		font-size: 11px;
		margin-left: 4px;
		opacity: 0.75;
		font-variant-numeric: tabular-nums;
	}
}
.re-issue {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 12px;
	flex-wrap: nowrap;
	&__label { font-size: 13px; font-weight: 600; white-space: nowrap; }
	&__name { width: 240px; flex: 0 0 auto; }
	&__days { width: 84px; flex: 0 0 auto; height: 28px; padding-top: 2px; padding-bottom: 2px; font-size: 13px; }
	&__btn { white-space: nowrap; flex: 0 0 auto; }
	.re-hint { margin-left: 4px; }
}

/* ── 관심도(보조 패널) ─────────────────────────────────────────────────────── */
.re-interest {
	td { vertical-align: middle; }
	&__name {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	&__sub {
		white-space: nowrap;
		display: block;
		font-size: 11.5px;
		color: $dt-color-ink-faint;
		font-weight: 400;
		line-height: 1.3;
	}
}
.re-bar-cell { white-space: nowrap; }
.re-bar {
	display: inline-block;
	width: 44px;
	height: 8px;
	background: $dt-color-line;
	border-radius: 4px;
	overflow: hidden;
	vertical-align: middle;
	&__fill { display: block; height: 100%; background: $dt-color-primary-ink; }
	&__num { font-size: 12px; margin-left: 6px; color: $dt-color-ink-muted; font-variant-numeric: tabular-nums; }
}

/* ── 아래 조작 줄 ─────────────────────────────────────────────────────────── */
.re-foot {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	&__main { display: flex; gap: 8px; }
}
</style>
